import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Loader2, Zap, Clock, Info, Pencil, Send, RotateCcw, Save, X, CheckCircle2 } from 'lucide-react';
import {
  useMessageTemplates, useMessageTemplate, useSaveMessageTemplate, useDeleteMessageTemplate,
  useMessageSchedules, useSaveMessageSchedule, useTestMessageTemplate,
} from '../../../hooks/useManagement';
import { useClientAuth } from '../../../contexts/ClientAuthContext';
import EmailTemplateEditor, { GUEST_MERGE_TAGS, type EmailEditorHandle } from '../../../components/EmailTemplateEditor';
import { buildDefaultTemplate, type Lang } from '../../../lib/emailTemplateDefaults';
import { useConfirm } from '../../../components/ConfirmDialog';
import CustomMessagesSection from './CustomMessagesSection';
import { EMAIL_EDITOR_ENABLED } from '../../../config/features';

interface PreviewMessage { key: string; label: string; subject: string; html: string; custom?: boolean }

const LANGS: { code: Lang; label: string }[] = [
  { code: 'es', label: 'ES' }, { code: 'en', label: 'EN' }, { code: 'it', label: 'IT' }, { code: 'pl', label: 'PL' },
];

// When each automated / transactional guest message fires (Spanish defaults;
// i18n under portal.manage.autoMessages.when.<key>).
const TRIGGERS: Record<string, { when: string; auto?: boolean }> = {
  confirmed: { when: 'Al confirmarse el pago de una reserva' },
  transferRequest: { when: 'Cuando el huésped elige pagar por transferencia' },
  transferConfirmed: { when: 'Cuando confirmas que has recibido la transferencia' },
  cancelled: { when: 'Al cancelar una reserva' },
  prearrival: { when: '≈ 3 días antes de la llegada', auto: true },
  poststay: { when: '≈ 1 día después de la salida', auto: true },
  balanceCharged: { when: 'Al cobrar automáticamente el resto pendiente' },
  balanceDue: { when: 'Cuando queda un importe pendiente por pagar' },
};

export default function ManageAutoMessagesPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { user } = useClientAuth();
  const [messages, setMessages] = useState<PreviewMessage[]>([]);
  const [sel, setSel] = useState<string>('');
  const [lang, setLang] = useState<Lang>('es');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState('');
  const [saving, setSaving] = useState(false);
  const [seed, setSeed] = useState<unknown | null | 'loading'>('loading');
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const editorRef = useRef<EmailEditorHandle>(null);
  const flash = (kind: 'success' | 'error', msg: string) => { setToast({ kind, msg }); setTimeout(() => setToast(null), 4000); };

  const { data: overrides } = useMessageTemplates();
  const { data: schedules } = useMessageSchedules();
  const { data: fullTemplate, isLoading: tplLoading } = useMessageTemplate(editing ? sel : null, lang);
  const saveTpl = useSaveMessageTemplate();
  const delTpl = useDeleteMessageTemplate();
  const saveSchedule = useSaveMessageSchedule();
  const testSend = useTestMessageTemplate();

  // Load previews for the selected language (re-fetch on lang change).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/portal/manage/message-previews?lang=${lang}`, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('fetch'))))
      .then((d) => { const data = d as { messages: PreviewMessage[] }; if (!cancelled) { setMessages(data.messages); setSel(s => s || data.messages[0]?.key || ''); } })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lang]);

  const active = useMemo(() => messages.find(m => m.key === sel), [messages, sel]);
  // Trigger labels come from the server in Spanish; localize them client-side
  // (fallback to the server label) so the list matches the selected language.
  const labelOf = (m: PreviewMessage) => t(`portal.manage.autoMessages.label.${m.key}`, m.label);
  const overrideOf = (key: string) => (overrides ?? []).find(o => o.trigger_key === key && o.lang === lang);
  const activeOverride = sel ? overrideOf(sel) : undefined;
  const scheduleOf = (key: string) => (schedules ?? []).find(s => s.trigger_key === key);

  useEffect(() => {
    if (!editing) return;
    setSubject(activeOverride?.subject ?? active?.subject ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, sel, lang]);

  // Compute the editor seed: saved template if present, else a fully-filled
  // localized default for the trigger — so the editor never opens blank.
  useEffect(() => {
    if (!editing || tplLoading) { setSeed('loading'); return; }
    let cancelled = false;
    (async () => {
      if (fullTemplate?.body_json) {
        try { const j = JSON.parse(fullTemplate.body_json); if (!cancelled) setSeed(j); return; } catch { /* fall through */ }
      }
      try { const def = await buildDefaultTemplate(sel, lang); if (!cancelled) setSeed(def); }
      catch { if (!cancelled) setSeed(null); }
    })();
    return () => { cancelled = true; };
  }, [editing, tplLoading, fullTemplate, sel, lang]);

  const handleSave = async () => {
    if (!editorRef.current || !sel) return;
    if (!subject.trim()) { flash('error', t('portal.manage.autoMessages.errSubject', 'Indica un asunto.')); return; }
    setSaving(true);
    try {
      const html = await editorRef.current.compileHtml();
      const json = editorRef.current.getContent();
      await saveTpl.mutateAsync({ trigger_key: sel, lang, subject: subject.trim(), body_json: json, body_html: html, enabled: true });
      flash('success', t('portal.manage.autoMessages.saved', 'Plantilla guardada. Este mensaje ahora usa tu diseño.'));
      setEditing(false);
    } catch (e) {
      console.error(e);
      flash('error', t('portal.manage.autoMessages.errSave', 'No se ha podido guardar la plantilla.'));
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!sel) return;
    if ((await confirm({
      title: t('portal.manage.autoMessages.resetTitle', 'Restaurar mensaje predeterminado'),
      message: t('portal.manage.autoMessages.resetConfirm', '¿Eliminar tu plantilla personalizada y volver al mensaje predeterminado de Kanarix?'),
      confirmLabel: t('portal.manage.autoMessages.resetYes', 'Restaurar'),
      tone: 'danger',
    })) !== 'confirm') return;
    await delTpl.mutateAsync({ trigger_key: sel, lang });
    setEditing(false);
    flash('success', t('portal.manage.autoMessages.resetDone', 'Mensaje predeterminado restaurado.'));
  };

  const openTest = () => { setTestTo(user?.email ?? ''); setTestOpen(true); };
  const handleTest = async () => {
    if (!sel) return;
    try {
      const r = await testSend.mutateAsync({ trigger_key: sel, to: testTo.trim() || undefined, lang });
      setTestOpen(false);
      flash(r.ok ? 'success' : 'error', r.ok
        ? t('portal.manage.autoMessages.testSent', 'Email de prueba enviado a {{to}}', { to: r.to })
        : t('portal.manage.autoMessages.testFail', 'No se ha podido enviar la prueba.'));
    } catch { flash('error', t('portal.manage.autoMessages.testFail', 'No se ha podido enviar la prueba.')); }
  };

  const sched = sel ? scheduleOf(sel) : undefined;

  const LangSwitch = (
    <div className="inline-flex items-center rounded-lg bg-slate-100 p-0.5">
      {LANGS.map(l => (
        <button key={l.code} onClick={() => setLang(l.code)}
          className={`px-2.5 py-1 text-xs font-semibold rounded-md ${lang === l.code ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          {l.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="w-full space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-[110] rounded-xl px-4 py-3 text-sm font-medium shadow-lg border ${toast.kind === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-rose-50 text-rose-800 border-rose-200'}`} role="status">{toast.msg}</div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Mail size={22} className="text-teal-600" /> {t('portal.manage.autoMessages.title', 'Mensajes automáticos')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('portal.manage.autoMessages.subtitle', 'Los emails que tus huéspedes reciben automáticamente en cada momento de la reserva, con tu marca.')}</p>
        </div>
        {/* The preview itself is localized (server renders per-lang), so the
            switcher is always available — even when the editor is disabled. */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{t('portal.manage.autoMessages.language', 'Idioma')}</span>
          {LangSwitch}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal-500" size={28} /></div>
      ) : error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">{t('portal.manage.autoMessages.error', 'No se han podido cargar los mensajes.')}</div>
      ) : (
        <div className="grid lg:grid-cols-[340px_1fr] gap-5 items-start">
          {/* Trigger list */}
          <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
            {messages.map(m => {
              const tr = TRIGGERS[m.key];
              const on = sel === m.key;
              const custom = !!overrideOf(m.key);
              return (
                <button key={m.key} onClick={() => { setSel(m.key); setEditing(false); }} className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${on ? 'bg-teal-50/70' : ''}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${tr?.auto ? 'bg-teal-100 text-teal-600' : 'bg-slate-100 text-slate-500'}`}>{tr?.auto ? <Clock size={15} /> : <Zap size={15} />}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                        {labelOf(m)}
                        {custom && <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 shrink-0">{t('portal.manage.autoMessages.customBadge', 'Personalizado')}</span>}
                      </div>
                      <div className="text-xs text-slate-400 truncate">{tr ? t(`portal.manage.autoMessages.when.${m.key}`, tr.when) : ''}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Preview / editor */}
          <div className="space-y-3">
            {active && !editing && (
              <>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wide font-semibold"><Info size={13} /> {t('portal.manage.autoMessages.trigger', 'Se envía')}</div>
                      <div className="mt-1 text-sm text-slate-700">{(() => { const tr = TRIGGERS[active.key]; return tr ? t(`portal.manage.autoMessages.when.${active.key}`, tr.when) : ''; })()}</div>
                      <div className="mt-2 text-xs text-slate-400">{t('portal.manage.autoMessages.subject', 'Asunto')}: <span className="text-slate-600">{activeOverride?.subject ?? active.subject}</span></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={openTest} className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">
                        <Send size={14} /> {t('portal.manage.autoMessages.test', 'Enviarme una prueba')}
                      </button>
                      {EMAIL_EDITOR_ENABLED && activeOverride && (
                        <button onClick={handleReset} className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">
                          <RotateCcw size={14} /> {t('portal.manage.autoMessages.reset', 'Restaurar')}
                        </button>
                      )}
                      {EMAIL_EDITOR_ENABLED && (
                        <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium">
                          <Pencil size={14} /> {activeOverride ? t('portal.manage.autoMessages.edit', 'Editar plantilla') : t('portal.manage.autoMessages.customize', 'Personalizar')}
                        </button>
                      )}
                    </div>
                  </div>

                  {EMAIL_EDITOR_ENABLED && (active.key === 'prearrival' || active.key === 'poststay') && sched && (
                    <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-3 text-sm">
                      <Clock size={15} className="text-teal-600" />
                      <span className="text-slate-600">{active.key === 'prearrival' ? t('portal.manage.autoMessages.sendDaysBefore', 'Enviar días antes de la llegada:') : t('portal.manage.autoMessages.sendDaysAfter', 'Enviar días después de la salida:')}</span>
                      <input type="number" min={1} max={14} defaultValue={Math.abs(sched.offset_days)}
                        onBlur={e => {
                          const n = Math.max(1, Math.min(14, Math.trunc(Number(e.target.value) || 1)));
                          const signed = active.key === 'prearrival' ? -n : n;
                          if (signed !== sched.offset_days) saveSchedule.mutate({ trigger_key: active.key, offset_days: signed, enabled: !!sched.enabled });
                        }}
                        className="w-20 px-2 py-1.5 border border-slate-300 rounded-lg text-sm" />
                      <label className="inline-flex items-center gap-1.5 text-slate-600">
                        <input type="checkbox" defaultChecked={!!sched.enabled}
                          onChange={e => saveSchedule.mutate({ trigger_key: active.key, offset_days: sched.offset_days, enabled: e.target.checked })} />
                        {t('portal.manage.autoMessages.scheduleEnabled', 'Activo')}
                      </label>
                      {saveSchedule.isSuccess && <CheckCircle2 size={15} className="text-emerald-500" />}
                    </div>
                  )}
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <iframe key={`${sel}-${lang}`} title={labelOf(active)} srcDoc={active.html} className="w-full h-[640px] border-0 bg-white" sandbox="allow-same-origin" />
                </div>
              </>
            )}

            {EMAIL_EDITOR_ENABLED && active && editing && (
              <div className="space-y-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h2 className="font-semibold text-slate-900">{t('portal.manage.autoMessages.editorTitle', 'Editor de plantilla')} — {labelOf(active)} <span className="text-slate-400 font-normal">({lang.toUpperCase()})</span></h2>
                    <div className="flex items-center gap-2">
                      {LangSwitch}
                      <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-slate-900"><X size={14} /> {t('portal.manage.autoMessages.cancel', 'Cancelar')}</button>
                      {activeOverride && (
                        <button onClick={handleReset} className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"><RotateCcw size={14} /> {t('portal.manage.autoMessages.reset', 'Restaurar')}</button>
                      )}
                      <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t('portal.manage.autoMessages.save', 'Guardar plantilla')}
                      </button>
                    </div>
                  </div>
                  <label className="block">
                    <span className="block text-xs font-medium text-slate-600 mb-1">{t('portal.manage.autoMessages.subject', 'Asunto')}</span>
                    <input value={subject} onChange={e => setSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" />
                  </label>
                  <p className="text-xs text-slate-400 flex items-start gap-1.5"><Info size={13} className="mt-0.5 shrink-0" />
                    {t('portal.manage.autoMessages.mergeHint', 'Usa variables como {guest_name}, {property_name} o {check_in} — se sustituyen automáticamente al enviar. Encuéntralas en el botón de variables del editor.')}</p>
                </div>
                {seed === 'loading' ? (
                  <div className="flex justify-center py-16"><Loader2 className="animate-spin text-teal-500" size={26} /></div>
                ) : (
                  <EmailTemplateEditor key={`${sel}-${lang}`} ref={editorRef} initialContent={seed} mergeTags={GUEST_MERGE_TAGS} />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom owner-created automatic emails (dev-only for now) */}
      {EMAIL_EDITOR_ENABLED && <CustomMessagesSection flash={flash} />}

      {/* Test-send modal */}
      {testOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[120] p-4" onClick={() => setTestOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900 text-lg mb-1">{t('portal.manage.autoMessages.testTitle', 'Enviar prueba')}</h3>
            <p className="text-sm text-slate-500 mb-4">{t('portal.manage.autoMessages.testDesc', 'Enviaremos este mensaje con datos de ejemplo al email que indiques.')}</p>
            <label className="block mb-4">
              <span className="block text-xs font-medium text-slate-600 mb-1">{t('portal.manage.autoMessages.testEmail', 'Email de destino')}</span>
              <input type="email" value={testTo} onChange={e => setTestTo(e.target.value)} autoFocus
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" placeholder="tu@email.com" />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setTestOpen(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">{t('portal.manage.autoMessages.cancel', 'Cancelar')}</button>
              <button onClick={handleTest} disabled={testSend.isPending || !testTo.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {testSend.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {t('portal.manage.autoMessages.sendTest', 'Enviar prueba')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
